"use client";

import { cn } from "@/lib/utils";
import { CheckIcon } from "lucide-react"; // Using Lucide icons as placeholders for custom SVGs
import type { ReactNode } from "react";
import { Gems, UtilityArrowForward } from "./SharedComponents";

interface ActionItemProps {
  title: string;
  isCompleted: boolean;
  onClick?: () => void;
  icon?: ReactNode;
  rewardValue?: string;
  rewardLabel?: string;
  isLoading?: boolean;
}

export function ActionItem({
  title,
  isCompleted,
  onClick,
  icon,
  rewardValue = "Entry Secured",
  rewardLabel,
  isLoading,
}: ActionItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full relative group text-left transition-transform active:scale-[0.98]",
        "min-h-[50px] [@media(min-width:380px)]:min-h-[60px] sm:min-h-[64px] md:min-h-[34px] rounded-[4px] p-[1px]", // Smaller on desktop
        "bg-[rgba(247,247,247,0.06)]", // Base background
      )}
    >
      <div className="flex items-center justify-between p-3 [@media(min-width:380px)]:p-4 sm:p-4.5 md:p-1.5 gap-3 [@media(min-width:380px)]:gap-4 sm:gap-4 md:gap-1.5">
        {/* Icon Box */}
        <div className="relative size-7 [@media(min-width:380px)]:size-9 sm:size-10 md:size-6 shrink-0 rounded-[4px] border border-white/10 overflow-hidden flex items-center justify-center">
          <div className="relative z-10 text-white">
            {isCompleted ? (
              <CheckIcon className="size-4 [@media(min-width:380px)]:size-5 sm:size-5 md:size-3 text-[#abf790]" />
            ) : (
              icon
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-grow min-w-0 flex flex-col">
          <span
            className={cn(
              "text-sm [@media(min-width:380px)]:text-base sm:text-lg md:text-xs font-medium truncate transition-colors",
              isCompleted ? "text-[#abf790]" : "text-[#fafafa]",
            )}
          >
            {title}
          </span>
        </div>

        {/* Right Side: Reward or Action */}
        <div className="flex items-center gap-2 shrink-0">
          {isCompleted ? (
            <Gems success value={rewardValue} />
          ) : (
            <>
              {rewardLabel && (
                <div className="relative h-4 md:h-3.5 rounded-md border border-white/10 overflow-hidden flex items-center px-1.5 md:px-1">
                  <span className="relative z-10 text-[9px] md:text-[8px] bg-gradient-to-r from-[#D37A38] to-[#F5A623] bg-clip-text text-transparent uppercase font-bold">
                    {rewardLabel}
                  </span>
                </div>
              )}
              <div className="size-5 md:size-4 rounded-full border border-[#f3f3f3] flex items-center justify-center bg-[#f3f3f3]/10">
                {isLoading ? (
                  <div className="size-2.5 md:size-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <UtilityArrowForward className="text-white size-2.5 md:size-2" />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </button>
  );
}
